import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square } from 'lucide-react';

import { appelAuthentifie } from '../../services/api/assistants';
import { buildApiUrl } from '../../utils/apiUrl';

interface Props {
  /** Texte reconnu, à ajouter à la saisie. */
  onTexte: (texte: string) => void;
  disabled?: boolean;
}

type Etat = 'repos' | 'ecoute' | 'transcription';

/**
 * Dicter son message plutôt que le taper.
 *
 * Un clic lance l'écoute, un second l'arrête ; le texte transcrit s'ajoute à la
 * saisie, où on le relit avant d'envoyer. Rien ne part sans ce relu.
 */
const BoutonDictee: React.FC<Props> = ({ onTexte, disabled = false }) => {
  const [etat, setEtat] = useState<Etat>('repos');
  const [erreur, setErreur] = useState('');
  const [secondes, setSecondes] = useState(0);
  const enregistreur = useRef<MediaRecorder | null>(null);
  const morceaux = useRef<Blob[]>([]);
  const minuteur = useRef<number | null>(null);

  useEffect(() => () => {
    if (minuteur.current) window.clearInterval(minuteur.current);
    enregistreur.current?.stream.getTracks().forEach((piste) => piste.stop());
  }, []);

  if (typeof window === 'undefined' || !navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
    return null;
  }

  const transcrire = async (son: Blob) => {
    setEtat('transcription');
    try {
      const corps = new FormData();
      corps.append('audio', son, son.type.includes('mp4') ? 'dictee.m4a' : 'dictee.webm');
      const reponse = await appelAuthentifie(buildApiUrl('/documents/dictee/'), { method: 'POST', body: corps });
      const donnees = await reponse.json().catch(() => ({}));
      if (!reponse.ok) throw new Error(donnees.detail || "La dictée n'a pas pu être transcrite.");
      if (donnees.texte) onTexte(donnees.texte);
    } catch (e: any) {
      setErreur(e.message);
    } finally {
      setEtat('repos');
    }
  };

  const demarrer = async () => {
    setErreur('');
    try {
      const flux = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(flux);
      morceaux.current = [];
      rec.ondataavailable = (e) => e.data.size && morceaux.current.push(e.data);
      rec.onstop = () => {
        flux.getTracks().forEach((piste) => piste.stop());
        if (minuteur.current) window.clearInterval(minuteur.current);
        const son = new Blob(morceaux.current, { type: rec.mimeType || 'audio/webm' });
        if (son.size > 0) transcrire(son);
        else setEtat('repos');
      };
      enregistreur.current = rec;
      rec.start();
      setSecondes(0);
      minuteur.current = window.setInterval(() => setSecondes((s) => s + 1), 1000);
      setEtat('ecoute');
    } catch {
      setErreur("Le micro n'est pas accessible. Autorisez-le dans le navigateur.");
    }
  };

  const arreter = () => enregistreur.current?.state === 'recording' && enregistreur.current.stop();

  return (
    <span className="relative inline-flex items-center gap-1.5">
      {etat === 'ecoute' && (
        <span className="font-mono text-[11px] text-[#B42318]">
          {Math.floor(secondes / 60)}:{String(secondes % 60).padStart(2, '0')}
        </span>
      )}
      {etat === 'transcription' && <span className="text-[11px] text-eg-estompe">Transcription…</span>}
      <button
        type="button"
        onClick={etat === 'ecoute' ? arreter : demarrer}
        disabled={disabled || etat === 'transcription'}
        title={etat === 'ecoute' ? "Arrêter et transcrire" : 'Dicter le message'}
        aria-label={etat === 'ecoute' ? "Arrêter la dictée" : 'Dicter le message'}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors
          disabled:opacity-40 ${
            etat === 'ecoute'
              ? 'animate-pulse bg-[#B42318] text-white'
              : 'bg-black/[0.045] text-eg-gris hover:bg-black/[0.08] hover:text-eg-encre'
          }`}
      >
        {etat === 'transcription' ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : etat === 'ecoute' ? (
          <Square className="h-3.5 w-3.5" fill="currentColor" />
        ) : (
          <Mic className="h-4 w-4" />
        )}
      </button>
      {erreur && (
        <span className="absolute bottom-full right-0 mb-2 w-64 rounded-lg bg-eg-encre px-3 py-2 text-[11.5px] text-white shadow-lg">
          {erreur}
        </span>
      )}
    </span>
  );
};

export default BoutonDictee;
